import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Building2, Landmark, Sparkles, ShieldCheck, Globe2, Clock, MapPin,
  Hammer, Boxes, Rocket, TrendingUp, Code2, Smartphone, MonitorSmartphone, BrainCircuit, Cpu, FileCheck2, Headset, Lock, LayoutDashboard,
} from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Testimonials from "@/components/Testimonials";
import { setSeo, setJsonLd, orgJsonLd, websiteJsonLd, SITE_URL } from "@/lib/seo";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";
import { useAuth } from "@/context/AuthContext";
import { formatUSD } from "@/lib/constants";
import { IMAGES } from "@/lib/images";
import { SERVICE_GROUPS } from "@/lib/services";
import HomeInfo from "@/components/HomeInfo";

const FLOW = [
  { icon: Hammer, labelKey: "home.flowBuildLabel", bodyKey: "home.flowBuildBody", to: "/company-formation" },
  { icon: Boxes, labelKey: "home.flowStructureLabel", bodyKey: "home.flowStructureBody", to: "/more-services" },
  { icon: Landmark, labelKey: "home.flowBankLabel", bodyKey: "home.flowBankBody", to: "/banks" },
  { icon: ShieldCheck, labelKey: "home.flowOperateLabel", bodyKey: "home.flowOperateBody", to: "/more-services" },
  { icon: Rocket, labelKey: "home.flowGrowLabel", bodyKey: "home.flowGrowBody", to: "/digital-studio" },
];

const STUDIO_CAPS = [
  { icon: Globe2, labelKey: "home.studioCapWebsite", slug: "website" },
  { icon: Smartphone, labelKey: "home.studioCapAndroid", slug: "android" },
  { icon: Smartphone, labelKey: "home.studioCapIos", slug: "ios" },
  { icon: MonitorSmartphone, labelKey: "home.studioCapWebApp", slug: "webapp" },
  { icon: Cpu, labelKey: "home.studioCapSaas", slug: "saas" },
  { icon: BrainCircuit, labelKey: "home.studioCapAi", slug: "ai" },
  { icon: Code2, labelKey: "home.studioCapSoftware", slug: "software" },
];

const PROCESS = [
  { n: "01", tKey: "home.process1Title", bKey: "home.process1Body" },
  { n: "02", tKey: "home.process2Title", bKey: "home.process2Body" },
  { n: "03", tKey: "home.process3Title", bKey: "home.process3Body" },
  { n: "04", tKey: "home.process4Title", bKey: "home.process4Body" },
  { n: "05", tKey: "home.process5Title", bKey: "home.process5Body" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [featured, setFeatured] = useState([]);
  const [meta, setMeta] = useState({ regions: null, countries: null, banks: null, names: [] });

  useEffect(() => {
    setSeo({
      title: t("seo.homeTitle"),
      description: t("seo.homeDesc"),
      url: `${SITE_URL}/`,
    });
    setJsonLd("org-schema", orgJsonLd());
    setJsonLd("website-schema", websiteJsonLd());
    api.get("/packages").then((r) => { const ps = (r.data.packages || []).filter((p) => p.category === "Incorporation"); setFeatured((ps.filter((p) => p.popular).concat(ps).slice(0, 3))); }).catch(() => {});
    api.get("/catalog").then((r) => {
      const c = r.data.countries || [];
      const regs = new Set(c.map((x) => x.region).filter(Boolean));
      const bankNames = new Set((r.data.banks || []).map((b) => b.name));
      setMeta({ regions: regs.size, countries: c.length, banks: bankNames.size, names: (r.data.region_order || []).filter((rg) => c.some((x) => x.region === rg)) });
    }).catch(() => {});
  }, [t]);

  const jurisdictionCount = meta.countries != null ? meta.countries : null;
  const fromPrice = featured[0]?.price;

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative grain overflow-hidden pt-24 pb-12 lg:pt-32 lg:pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-700 border border-orange-200 bg-orange-50 rounded-sm px-3 py-1">
              <Sparkles className="h-3 w-3" /> {t("home.heroKicker")}
            </span>
            <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-[4.2rem] leading-[0.98] tracking-tight text-foreground">
              {t("home.heroTitle1")}
              <br />
              <span className="text-orange-600">{t("home.heroTitle2")}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t("home.heroSubtitle")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button size="lg" className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7 shadow-md" onClick={() => navigate("/company-formation")} data-testid="hero-cta-primary">
                {t("home.heroCtaPrimary")} <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-sm border-primary text-primary bg-transparent hover:bg-secondary h-12 px-7" onClick={() => navigate("/company-formation")} data-testid="hero-cta-explore">
                {t("home.heroCtaExplore")}
              </Button>
            </div>
            <button onClick={() => navigate("/contact")} className="mt-4 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-orange-700 transition-colors" data-testid="hero-cta-expert">
              {t("home.heroCtaExpert")} <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-orange-600" /> {t("home.trustPricing")}</span>
              <span className="flex items-center gap-2"><Globe2 className="h-4 w-4 text-orange-600" /> {jurisdictionCount != null ? `${jurisdictionCount} ${t("nav.jurisdictions")}` : t("home.trustJurisdictions")}</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-orange-600" /> {t("home.trustRemote")}</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }} className="relative">
            <div className="relative rounded-sm overflow-hidden ring-1 ring-border shadow-[0_20px_60px_-20px_rgb(10,17,40,0.25)]">
              <img src={IMAGES.hero} alt="Global business city skyline" className="w-full h-[440px] object-cover" width="2000" height="1333" fetchPriority="high" decoding="async" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/45 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-4 bg-card border border-border rounded-sm px-5 py-4 shadow-[0_10px_40px_-10px_rgb(10,17,40,0.18)] w-56">
              <ShieldCheck className="h-5 w-5 text-orange-600" />
              <p className="mt-2 text-sm text-foreground font-medium">{t("home.cardPricing")}</p>
              <p className="text-xs text-muted-foreground">{t("home.cardNoFees")}</p>
            </div>
            <div className="absolute -top-4 -right-3 bg-primary text-primary-foreground rounded-sm px-5 py-3 shadow-lg hidden sm:block">
              <p className="font-heading text-2xl leading-none">{jurisdictionCount != null ? jurisdictionCount : "—"}</p>
              <p className="text-[11px] font-mono uppercase tracking-wider text-orange-300 mt-1">{t("nav.jurisdictions")}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Choose your journey */}
      <section className="border-y border-border bg-secondary/30" data-testid="home-journeys">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 lg:py-14">
          <p className="text-xs uppercase tracking-[0.2em] text-orange-700 font-mono">{t("home.journeyKicker")}</p>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Building2, label: t("home.journeyStartTitle"), body: t("home.journeyStartBody"), to: "/company-formation", tid: "journey-start" },
              { icon: Landmark, label: t("home.journeyBankTitle"), body: t("home.journeyBankBody"), to: "/banks", tid: "journey-banking" },
              { icon: FileCheck2, label: t("home.journeyComplianceTitle"), body: t("home.journeyComplianceBody"), to: "/company-formation?category=Compliance", tid: "journey-compliance" },
              { icon: LayoutDashboard, label: t("home.journeySupportTitle"), body: t("home.journeySupportBody"), to: user ? "/dashboard" : "/login", tid: "journey-support" },
            ].map((j, i) => (
              <motion.button
                key={j.label} onClick={() => navigate(j.to)}
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
                className="group text-left rounded-2xl border border-border bg-card p-5 card-soft hover:border-orange-400 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgb(10,17,40,0.18)] transition-all"
                data-testid={j.tid}
              >
                <span className="grid place-items-center h-11 w-11 rounded-sm bg-secondary text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-colors"><j.icon className="h-5 w-5" /></span>
                <p className="mt-4 font-heading text-lg text-foreground group-hover:text-orange-700 transition-colors flex items-center gap-1.5">{j.label}<ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" /></p>
                <p className="mt-1 text-sm text-muted-foreground leading-snug">{j.body}</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-border bg-card/60" data-testid="home-trust-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
          {[
            { icon: ShieldCheck, t: t("home.trustSecuredTitle"), s: t("home.trustSecuredBody") },
            { icon: FileCheck2, t: t("home.trustScopeTitle"), s: t("home.trustScopeBody") },
            { icon: Headset, t: t("home.trustSpecialistTitle"), s: t("home.trustSpecialistBody") },
            { icon: Lock, t: t("home.trustPrivateTitle"), s: t("home.trustPrivateBody") },
          ].map((x) => (
            <div key={x.t} className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-sm bg-secondary text-orange-600 shrink-0"><x.icon className="h-5 w-5" /></span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight">{x.t}</p>
                <p className="text-xs text-muted-foreground leading-tight mt-0.5">{x.s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 1 — GLOBAL BUSINESS SOLUTIONS (flow) */}
      <section className="border-t border-border bg-secondary/30" data-testid="home-solutions">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-orange-700 font-mono">{t("home.solutionsKicker")}</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">{t("home.solutionsTitle")}</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">{t("home.solutionsBody")}</p>
          </div>
          <div className="mt-12 relative">
            <div className="hidden lg:block absolute top-7 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-300 to-transparent" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-8">
              {FLOW.map((f, i) => (
                <motion.button key={f.labelKey} onClick={() => navigate(f.to)} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.07 }}
                  className="group relative text-left" data-testid={`flow-${f.slug || t(f.labelKey).toLowerCase()}`}>
                  <span className="relative z-10 grid place-items-center h-14 w-14 rounded-full bg-card border border-border text-orange-600 shadow-sm group-hover:border-orange-400 group-hover:-translate-y-0.5 transition-all">
                    <f.icon className="h-6 w-6" />
                  </span>
                  <p className="mt-4 font-heading text-lg text-foreground group-hover:text-orange-700 transition-colors">{t(f.labelKey)}</p>
                  <p className="mt-1 text-sm text-muted-foreground leading-snug">{t(f.bodyKey)}</p>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2 — TWO PILLARS: Formation + Banking */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="home-pillars">
        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="group relative rounded-2xl overflow-hidden min-h-[380px] flex flex-col justify-end p-8 lg:p-10 ring-1 ring-border" data-testid="pillar-formation">
            <img src={IMAGES.officeMeeting} alt="Company formation" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/70 to-primary/20" />
            <div className="relative text-primary-foreground">
              <span className="grid place-items-center h-11 w-11 rounded-sm bg-white/10 backdrop-blur border border-white/15"><Building2 className="h-5 w-5 text-orange-300" /></span>
              <p className="mt-5 text-xs uppercase tracking-[0.2em] text-orange-300 font-mono">{t("home.pillarFormationKicker")}</p>
              <h3 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight">{t("home.pillarFormationTitle")}</h3>
              <p className="mt-3 text-primary-foreground/80 max-w-md">{t("home.pillarFormationBody")}{fromPrice ? ` — ${t("home.pillarFormationFrom", { price: formatUSD(fromPrice) })}` : ""}. {t("home.pillarFormationBodyExtra")}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button className="rounded-sm bg-orange-600 text-white hover:bg-orange-500 h-11 px-6" onClick={() => navigate("/company-formation")} data-testid="pillar-formation-cta">{t("home.pillarFormationCta")}</Button>
                <Button variant="outline" className="rounded-sm border-white/30 text-white bg-transparent hover:bg-white/10 h-11 px-6" onClick={() => navigate("/company-formation")} data-testid="pillar-formation-start">{t("home.pillarFormationStart")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.08 }}
            className="group relative rounded-2xl overflow-hidden min-h-[380px] flex flex-col justify-end p-8 lg:p-10 ring-1 ring-border" data-testid="pillar-banking">
            <img src={IMAGES.banking} alt="Business banking" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/70 to-primary/20" />
            <div className="relative text-primary-foreground">
              <span className="grid place-items-center h-11 w-11 rounded-sm bg-white/10 backdrop-blur border border-white/15"><Landmark className="h-5 w-5 text-orange-300" /></span>
              <p className="mt-5 text-xs uppercase tracking-[0.2em] text-orange-300 font-mono">{t("home.pillarBankingKicker")}</p>
              <h3 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight">{t("home.pillarBankingTitle")}</h3>
              <p className="mt-3 text-primary-foreground/80 max-w-md">{t("home.pillarBankingBody")}{meta.banks ? ` ${t("home.pillarBankingBodyWith", { count: meta.banks })}` : ""}{t("home.pillarBankingBodyExtra")}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button className="rounded-sm bg-orange-600 text-white hover:bg-orange-500 h-11 px-6" onClick={() => navigate("/banks")} data-testid="pillar-banking-cta">{t("home.pillarBankingCta")}</Button>
                <Button variant="outline" className="rounded-sm border-white/30 text-white bg-transparent hover:bg-white/10 h-11 px-6" onClick={() => navigate("/banks")} data-testid="pillar-banking-open">{t("home.pillarBankingOpen")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social proof — coverage (DB-driven, honest) */}
      <section className="bg-primary text-primary-foreground" data-testid="home-coverage">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <p className="text-xs uppercase tracking-[0.2em] text-orange-300 font-mono max-w-[16rem]">{t("home.coverageKicker")}</p>
            <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-5">
              {[[t("home.coverageJurisdictions"), jurisdictionCount], [t("home.coverageRegions"), meta.regions], [t("home.coverageBankingOptions"), meta.banks]].map(([label, val]) => (
                <div key={label} className="text-center">
                  <p className="font-heading text-3xl sm:text-4xl leading-none">{val != null ? `${val}+` : "—"}</p>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-primary-foreground/60 mt-1.5">{label}</p>
                </div>
              ))}
            </div>
          </div>
          {(meta.names || []).length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-center gap-2 justify-center md:justify-start" data-testid="coverage-regions">
              <span className="text-[11px] font-mono uppercase tracking-wider text-primary-foreground/50 mr-1">{t("home.coverageRegionsLabel")}</span>
              {meta.names.map((rg) => (
                <button key={rg} onClick={() => navigate(`/company-formation?region=${encodeURIComponent(rg)}`)} className="rounded-full border border-white/20 bg-white/[0.06] hover:bg-white/[0.12] hover:border-orange-300/60 px-3.5 py-1.5 text-xs text-primary-foreground/90 transition-colors" data-testid={`coverage-region-${rg.toLowerCase().replace(/[^a-z]/g, "")}`}>{rg}</button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Client testimonials (admin-managed, DB-driven) */}
      <Testimonials />

      {/* 3 — MORE SERVICES ecosystem (dynamic) */}
      <section className="border-y border-border bg-secondary/30" data-testid="home-more-services">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-xl">
              <p className="text-xs uppercase tracking-[0.2em] text-orange-700 font-mono">{t("home.ecosystemKicker")}</p>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">{t("home.ecosystemTitle")}</h2>
            </div>
            <Button variant="outline" className="rounded-sm border-slate-300 bg-card hover:bg-secondary h-11 px-6 shrink-0" onClick={() => navigate("/more-services")} data-testid="more-services-explore">{t("home.ecosystemCta")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_GROUPS.map((g, i) => (
              <motion.div key={g.key} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}
                className="rounded-xl border border-border bg-card p-6 card-soft">
                <p className="font-heading text-lg text-foreground">{g.title}</p>
                <p className="mt-1.5 text-sm text-muted-foreground leading-snug min-h-[40px]">{g.blurb}</p>
                <div className="mt-4 space-y-1.5">
                  {g.items.filter((it) => it.id !== "digital-studio").slice(0, 4).map((it) => (
                    <button key={it.id} onClick={() => navigate(it.to || "/service/" + it.id)} className="group flex items-center gap-1.5 text-sm text-slate-600 hover:text-orange-700 transition-colors" data-testid={`home-svc-${it.id}`}>
                      <span className="h-1 w-1 rounded-full bg-orange-400" /> {it.title}
                    </button>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — DIGITAL STUDIO (standout) */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground grain" data-testid="home-digital-studio">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-orange-600/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-orange-500/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-orange-300 font-mono border border-white/15 rounded-sm px-3 py-1"><Sparkles className="h-3 w-3" /> {t("home.studioKicker")}</span>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
              {t("home.studioTitle1")} <span className="text-orange-400">{t("home.studioTitleAccent")}</span> {t("home.studioTitle2")}
            </h2>
            <p className="mt-5 text-lg text-primary-foreground/75 max-w-lg leading-relaxed">{t("home.studioBody")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-sm bg-orange-600 text-white hover:bg-orange-500 h-12 px-7 shadow-lg" onClick={() => navigate("/digital-studio")} data-testid="home-studio-build">{t("home.studioBuild")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              <Button size="lg" variant="outline" className="rounded-sm border-white/30 text-white bg-transparent hover:bg-white/10 h-12 px-7" onClick={() => navigate("/digital-studio")} data-testid="home-studio-estimate">{t("home.studioEstimate")}</Button>
            </div>
          </div>
          <div className="relative">
            <div className="grid grid-cols-2 gap-3">
              {STUDIO_CAPS.map((c, i) => (
                <motion.div key={c.labelKey} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                  className={`rounded-xl border border-white/10 bg-white/[0.06] backdrop-blur px-5 py-6 hover:bg-white/[0.1] transition-colors ${i === STUDIO_CAPS.length - 1 ? "col-span-2" : ""}`}>
                  <c.icon className="h-6 w-6 text-orange-300" />
                  <p className="mt-3 text-sm text-primary-foreground/90">{t(c.labelKey)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5 — UNI AI concierge */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="home-uni-ai">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="relative rounded-2xl border border-border bg-card overflow-hidden card-soft p-8 lg:p-14 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-orange-700 font-mono">{t("home.aiKicker")}</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">{t("home.aiTitle")}</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed max-w-lg">{t("home.aiBody")}</p>
            <Button size="lg" className="mt-7 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7" onClick={() => navigate("/advisor")} data-testid="home-ai-cta">
              <Sparkles className="h-4 w-4 mr-2 text-orange-400" /> {t("common.askAi")}
            </Button>
          </div>
          <div className="relative hidden lg:block">
            <div className="rounded-xl border border-border bg-secondary/50 p-5 space-y-3">
              <div className="flex justify-end"><span className="rounded-2xl rounded-br-sm bg-primary text-primary-foreground text-sm px-4 py-2 max-w-[80%]">{t("home.aiChatUser")}</span></div>
              <div className="flex justify-start"><span className="rounded-2xl rounded-bl-sm bg-card border border-border text-sm px-4 py-2 max-w-[85%] text-foreground">{t("home.aiChatBot")}</span></div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-70 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-orange-500" /></span> {t("home.aiTyping")}</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Self-contained scope, engagement, costs, guidance & FAQs (original content) */}
      <HomeInfo />

      {/* 6 — PROCESS */}
      <section className="border-t border-border bg-secondary/30" data-testid="home-process">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
          <div className="max-w-xl">
            <p className="text-xs uppercase tracking-[0.2em] text-orange-700 font-mono">{t("home.processKicker")}</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground">{t("home.processTitle")}</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {PROCESS.map((p, i) => (
              <motion.div key={p.n} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}
                className="relative">
                <span className="font-heading text-3xl text-orange-600/30">{p.n}</span>
                <h3 className="mt-2 font-heading text-lg text-foreground">{t(p.tKey)}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-snug">{t(p.bKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="relative rounded-2xl overflow-hidden bg-primary text-primary-foreground px-8 py-16 sm:px-14 text-center grain">
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t("home.ctaTitle")}</h2>
          <p className="mt-4 text-primary-foreground/70 max-w-xl mx-auto">{t("home.ctaSubtitle")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" className="rounded-sm bg-orange-600 text-white hover:bg-orange-500 h-12 px-8 shadow-lg" onClick={() => { trackEvent("begin_application"); navigate("/company-formation"); }} data-testid="cta-bottom">
              {t("common.getStarted")} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <Button size="lg" variant="outline" className="rounded-sm border-white/30 text-white bg-transparent hover:bg-white/10 h-12 px-8" onClick={() => navigate("/advisor")} data-testid="cta-bottom-ai">
              <Sparkles className="h-4 w-4 mr-1.5 text-orange-300" /> {t("common.askAi")}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
